import React, { useState, useEffect } from 'react';
import { GOOGLEMAPSAPIKEY } from '../../../config'
import { Autocomplete, Box, IconButton, Stack, TextField, Typography } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import moment from 'moment';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ReplayIcon from '@mui/icons-material/Replay';
import MapMarker from 'src/container/MapsBar/MapMarker';
import GoogleMapReact from 'google-map-react';
import RecurrenceModal from '../TaskForm/Date&Loc/RecurrenceModal';

const DATETIME_FORMAT = 'YYYY-MM-DDTHH:mm:ss';

const RECURRENCE_TEXT = { day: 'Daily', month: 'Monthly', year: 'Yearly' };

const buildRecurrenceText = (option, days) => {
    if (option !== 'week') return RECURRENCE_TEXT[option] || '';
    return days.reduce((acc, day, i) => acc + (i === 0 ? '' : ' and ') + day, 'Weekly on ');
};

const buildLocationOption = (loc) => ({
    value: loc.id,
    title: `${loc.location_addr1} ${loc.location_addr2}, ${loc.location_city}, ${loc.location_state}, ${loc.location_zip}`,
});

const DateLocationCard = (props) => {
    const locList = props.locList.map(buildLocationOption);

    const [showModal, setShowModal] = useState(false);
    const [recText, setRecText] = useState("Make Recurrence");
    const [saved, setSaved] = useState(false);

    const handleClose = () => setShowModal(false);
    const handleShow = () => setShowModal(true);

    const [num, setNum] = useState(1);
    const [option, setOption] = useState("week");
    const [days, setDays] = useState(["Mon"]);
    const [endOnDate, setEndOnDate] = useState(moment());
    const [disabled, setDisabled] = useState(false);
    const [numberOfRepeats, setNumberOfRepeats] = useState(1);
    const [numLocs, setNumLocs] = useState(props.formVals.tLoc.length > 0 ? props.formVals.tLoc.length : 1);
    const [tLoc, setTLoc] = useState(props.formVals.tLoc.length > 0 ? props.formVals.tLoc : []);

    useEffect(() => {
        setTLoc(props.formVals.tLoc)
    }, [props.formVals.tLoc]);

    const handleSubmit = () => {
        const recDets = {
            num,
            option,
            days: option === 'week' ? days : [],
            endsOn: disabled ? null : moment(endOnDate).format('YYYY-MM-DD'),
            numberOfRepeats: disabled ? numberOfRepeats : 0,
        };
        setRecText(buildRecurrenceText(option, days));
        setSaved(true);
        props.handleRecurrenceSubmit(recDets);
        handleClose();
    }

    const handleCancel = () => {
        handleClose();
        if (!saved) resetRecDets();
    }

    const resetRecDets = () => {
        setSaved(false);
        props.handleRecurrenceCancel();
        setRecText("Make Recurrence");
        setNum(1);
        setOption("week");
        setDays(["Mon"]);
        setEndOnDate(moment());
        setDisabled(false);
        setNumberOfRepeats(0);
    }

    const handleTrashClick = () => {
        setSaved(false);
        resetRecDets();
    }

    const handleCheckBox = (e) => {
        let name = e.target.name;
        if (e.target.checked === true) {
            setDays([...days, name]);
        }
        if (e.target.checked === false) {
            if (days.length <= 1) {
                e.target.checked = true;
            } else {
                setDays(days.filter((day) => day !== name));
            }
        }
    }

    const errFirstLoc = props.submitAttempted && !tLoc[0]?.value;

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Stack spacing={2}>
                <DateTimePicker
                    label="Task Start Time"
                    value={dayjs(props.formVals.tStrtDate).isValid() ? dayjs(props.formVals.tStrtDate) : null}
                    onChange={(d) => { props.handleProjLoc(dayjs(d).format(DATETIME_FORMAT), 'taskStartTime') }}
                    slotProps={{ textField: { variant: 'outlined', size: 'small', fullWidth: true, required: true } }}
                />
                <DateTimePicker
                    label="Task End Time"
                    value={dayjs(props.formVals.tEndDate).isValid() ? dayjs(props.formVals.tEndDate) : null}
                    onChange={(d) => { props.handleProjLoc(dayjs(d).format(DATETIME_FORMAT), 'taskEndTime') }}
                    slotProps={{
                        textField: {
                            variant: 'outlined',
                            size: 'small',
                            fullWidth: true,
                            required: true,
                            error: !!props.dateError,
                            helperText: props.dateError || '',
                        },
                    }}
                />

                {!props.edit && (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                width: '100%',
                                mr: 1,
                                cursor: 'pointer',
                                color: '#1976d2',
                                '&:hover .recurrence-label': { textDecoration: 'underline' },
                            }}
                            onClick={handleShow}
                        >
                            <IconButton sx={{ color: '#1976d2' }}>
                                <ReplayIcon />
                            </IconButton>
                            <Typography className="recurrence-label" sx={{ color: '#1976d2' }}>{recText}</Typography>
                        </Box>
                        <IconButton hidden={!saved} onClick={handleTrashClick}>
                            <DeleteIcon />
                        </IconButton>
                    </Box>
                )}

                {!props.edit && (
                    <RecurrenceModal
                        showModal={showModal}
                        project={props.project}
                        setRecText={setRecText}
                        setNum={setNum} setOption={setOption} setDays={setDays} setDisabled={setDisabled}
                        setEndOnDate={setEndOnDate} setNumberOfRepeats={setNumberOfRepeats}
                        num={num} option={option} days={days} endOnDate={endOnDate}
                        numberOfRepeats={numberOfRepeats} disabled={disabled}
                        handleSubmit={handleSubmit} handleCancel={handleCancel} handleCheckBox={handleCheckBox}
                        handleClose={handleClose}
                        handleRecurrenceSubmit={props.handleRecurrenceSubmit}
                        handleRecurrenceCancel={props.handleRecurrenceCancel}
                    />
                )}

                <Stack spacing={1.5}>
                    {Array(numLocs).fill(0).map((_, i) => {
                        const showErr = i === 0 && errFirstLoc;
                        return (
                            <Box key={i} sx={{ display: 'flex', alignItems: 'center' }}>
                                <Autocomplete
                                    id="tLoc"
                                    sx={{ width: '100%', mr: 1 }}
                                    size="small"
                                    value={tLoc[i]}
                                    disableClearable
                                    onChange={(e, newValue) => {
                                        let newTLoc = [...tLoc]
                                        newTLoc[i] = newValue
                                        props.handleMapsPin({ id: 'tLoc', value: newTLoc })
                                        return
                                    }}
                                    options={locList.filter((loc) => !tLoc.map((l) => l?.value).includes(loc?.value))}
                                    getOptionLabel={(option) => option.title !== undefined ? option.title : option}
                                    renderInput={(params) => <TextField {...params} label="Location" required variant="outlined" error={showErr} helperText={showErr ? 'Location is required' : ''} />}
                                />
                                <IconButton onClick={() => {
                                    setNumLocs(Math.max(numLocs - 1, 1));
                                    props.handleMapsPin({ id: 'tLoc', value: tLoc.filter((_, indx) => indx !== i) })
                                }} disabled={numLocs <= 1}>
                                    <DeleteIcon />
                                </IconButton>
                            </Box>
                        );
                    })}
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <IconButton sx={{ color: '#1976d2' }} onClick={() => setNumLocs(Math.min(numLocs + 1, 3))} disabled={numLocs >= 3}>
                            <AddIcon />
                        </IconButton>
                        <Typography sx={{ color: '#1976d2' }}>Add Location</Typography>
                    </Box>

                    <Autocomplete
                        id="tOloc"
                        sx={{ width: '100%' }}
                        size="small"
                        value={(props.officeLocList || []).find((o) => o.value === props.tOloc) || null}
                        onChange={(e, newValue) => props.onOfficeLocChange(newValue ? newValue.value : null)}
                        options={props.officeLocList || []}
                        isOptionEqualToValue={(option, val) => option.value === val?.value}
                        getOptionLabel={(option) => option?.title ?? ''}
                        renderInput={(params) => <TextField {...params} label="Office Location (optional)" variant="outlined" />}
                    />
                </Stack>

                {(props.mapRender === 'task' || props.edit) && (
                    <Box sx={{ width: '100%', height: 220 }}>
                        <GoogleMapReact
                            bootstrapURLKeys={{ key: GOOGLEMAPSAPIKEY }}
                            center={props.currentPin}
                            defaultZoom={10}
                            options={{ fullscreenControl: false, mapTypeControl: false }}
                            onGoogleApiLoaded={() => { }}
                            yesIWantToUseGoogleMapApiInternals
                        >
                            {props.mapsPushPin.length !== 0 && props.mapsPushPin.map((loc) => (
                                <MapMarker
                                    key={0}
                                    lat={loc.location[0]}
                                    lng={loc.location[1]}
                                    onClick={() => { }}
                                />
                            ))}
                        </GoogleMapReact>
                    </Box>
                )}
            </Stack>
        </LocalizationProvider>
    );
};

export default DateLocationCard;
